// @ts-nocheck
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Persists column widths in localStorage. Each table uses its own
 * key (storageKey). Originally lived in src/pages/dashboard/_hooks/ —
 * promoted to customHooks/ so it can be used in Event Requests, Estimates and
 * Approved Events.
 *
 * Usage:
 *   const { widths, setWidth, resetWidth, resetAllWidths } =
 *     useColumnWidths("eventRequestColumnWidths.v1");
 */
export const useColumnWidths = (storageKey) => {
  const readStorage = useCallback(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, [storageKey]);

  const [widths, setWidths] = useState(readStorage);
  const writeTimer = useRef(null);

  useEffect(() => {
    if (writeTimer.current) clearTimeout(writeTimer.current);
    writeTimer.current = setTimeout(() => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(widths));
      } catch {
        /* ignore quota errors */
      }
    }, 150);
    return () => {
      if (writeTimer.current) clearTimeout(writeTimer.current);
    };
  }, [widths, storageKey]);

  const setWidth = useCallback((columnId, px) => {
    setWidths((prev) => ({ ...prev, [columnId]: Math.round(px) }));
  }, []);

  const resetWidth = useCallback((columnId) => {
    setWidths((prev) => {
      const next = { ...prev };
      delete next[columnId];
      return next;
    });
  }, []);

  const resetAllWidths = useCallback(() => {
    setWidths({});
  }, []);

  return { widths, setWidth, resetWidth, resetAllWidths };
};
